import { useMemo, useState } from "react";

import Button from "@/components/Button";
import Checkbox from "@/components/Checkbox";
import RankedSelection from "@/components/RankedSelection";
import SearchInput from "@/components/SearchInput";
import { US_STATE_OPTIONS } from "./careerIntentCatalog";
import {
  moveRankedSelection,
  RANKED_LOCATION_HINT,
  rankedSelectionIndex,
  toggleRankedSelection
} from "./careerIntentForm";

function rankedOptionLabel(label: string, rank?: number) {
  return rank != null ? `${rank}. ${label}` : label;
}

export default function CareerIntentStatePicker({
  disabled = false,
  onChange,
  value
}: {
  disabled?: boolean;
  onChange: (value: string[]) => void;
  value: string[];
}) {
  const [isOpen, setIsOpen] = useState(() => value.length === 0);
  const [search, setSearch] = useState("");
  const normalizedSearch = search.trim().toLowerCase();
  const atMaxSelections = value.length >= 3;

  const visibleStates = useMemo(
    () =>
      US_STATE_OPTIONS.filter(
        (option) =>
          !normalizedSearch ||
          option.label.toLowerCase().includes(normalizedSearch) ||
          option.value.toLowerCase().includes(normalizedSearch)
      ),
    [normalizedSearch]
  );

  const toggleState = (stateCode: string) => {
    if (disabled) return;
    onChange(toggleRankedSelection(value, stateCode));
  };

  return (
    <div>
      <p className="mb-6 text-body-large text-sys-brand-on-surface-variant">{RANKED_LOCATION_HINT}</p>
      <RankedSelection
        className="mb-8"
        disabled={disabled}
        items={value.flatMap((stateCode) => {
          const state = US_STATE_OPTIONS.find((option) => option.value === stateCode);
          return state ? [{ value: state.value, label: state.label }] : [];
        })}
        onMove={(index, direction) => onChange(moveRankedSelection(value, index, direction))}
        onRemove={(stateCode) => onChange(toggleRankedSelection(value, stateCode))}
        testIdPrefix="career-intent-state"
      />

      <div className="flex flex-col gap-6" data-testid="career-intent-state-picker">
        <div className="self-start">
          <Button
            type="button"
            variant="text"
            ariaExpanded={isOpen}
            ariaLabel="Select States"
            testId="career-intent-state-picker-toggle"
            disabled={disabled}
            icon={isOpen ? "expand_less" : "expand_more"}
            trailingIcon
            onClick={() => setIsOpen((current) => !current)}
          >
            Select States
          </Button>
        </div>
        {isOpen ? (
          <div
            id="career-intent-state-picker-panel"
            className="flex flex-col gap-6 rounded-lg border border-sys-brand-outline p-4"
            data-testid="career-intent-state-picker-panel"
          >
            <div data-testid="career-intent-state-search">
              <SearchInput
                ariaLabel="Search States"
                pageSearchShortcut={false}
                placeholder="Search States"
                query={search}
                onChange={setSearch}
              />
            </div>
            <div
              className="flex max-h-80 flex-col gap-3 overflow-y-auto"
              data-testid="career-intent-state-picker-options"
            >
              {visibleStates.length === 0 ? (
                <p className="text-body-medium text-sys-brand-on-surface-variant">No states match your search</p>
              ) : (
                visibleStates.map((state) => {
                  const selected = value.includes(state.value);
                  const optionDisabled = disabled || (!selected && atMaxSelections);
                  const rank = rankedSelectionIndex(value, state.value);

                  return (
                    <Checkbox
                      key={state.value}
                      checked={selected}
                      disabled={optionDisabled}
                      name={`career-intent-state-option-${state.value}`}
                      label={rankedOptionLabel(state.label, rank)}
                      onChange={(checked) => {
                        if (checked === selected) return;
                        toggleState(state.value);
                      }}
                    />
                  );
                })
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
